import * as fabricTypes from 'fabric'
import { ObjectType } from '~/static/enums/object-type'
import { EditorObject, LayoutContext } from '@/utils/editor/object.class'
import { lines, PADDING } from '~/static/enums/lines'
import { FillType } from '~/static/enums/components/color-input'
import { ActualVersion, ActualVersionNumber } from '~/utils/editor/versioning/line'
import { assignNewColorAndAlpha } from '~/utils/ColorHelper'

const DefaultLength = 232
const DefaultPointerHeight = 0
const DefaultWidth = 1

export class Line extends EditorObject {
  length: number = DefaultLength
  pointerHeight: number = DefaultPointerHeight
  width: number = DefaultWidth
  height: number = PADDING * 2 + 1
  color: string = 'rgba(0,0,0,1)'
  opacity: number = 100
  dash: boolean = false
  dashSize: number[] = [ 5, 5 ]
  shadow: boolean = false
  shadowColor: string = 'rgba(0,0,0,1)'
  shadowOpacity: number = 100
  shadowX: number = 0
  shadowY: number = 4
  shadowBlur: number = 4
  objectIndex!: number

  constructor(props: ActualVersion) {
    super(ObjectType.Line)
    if (props.objectIndex === undefined) {
      throw createError('Line index not set: ' + JSON.stringify(props))
    }
    this.assignProperties({
      ...lines[props.objectIndex].defaultProps,
      ...props,
    })
  }

  load(): Promise<void> {
    this.setObject(lines[this.objectIndex].makeObject(<Line>{
      length: DefaultLength,
      pointerHeight: DefaultPointerHeight,
      width: DefaultWidth,
      color: 'rgba(0,0,0,1)',
      opacity: 100,
      dash: false,
      dashSize: [ 5, 5 ],
      shadow: false,
      shadowColor: 'rgba(0,0,0,1)',
      shadowOpacity: 100,
      shadowX: 0,
      shadowY: 4,
      shadowBlur: 4,
      ...lines[this.objectIndex].defaultProps,
    }))

    if (lines[this.objectIndex].disablePointerHeight) {
      this.height = this.width + PADDING * 2
    } else {
      this.height = this.pointerHeight + PADDING * 2
    }

    return Promise.resolve()
  }

  applyInitialLayout(context: LayoutContext): void {
    this.length = Math.round(this.length / context.zoom)
    this.pointerHeight = Math.round(this.pointerHeight / context.zoom)
    this.width = Math.round(this.width / context.zoom)
    if (lines[this.objectIndex].disablePointerHeight) {
      this.height = this.width + PADDING * 2
    } else {
      this.height = this.pointerHeight + PADDING * 2
    }
  }

  applyDefaultName(): void {}

  async set(props: ActualVersion): Promise<void> {
    this.assignProperties(props)
    return this.applyProps()
  }

  protected assignProperties(props: ActualVersion): void {
    super.assignProperties(props)
    if (lines[this.objectIndex].disablePointerHeight) {
      this.height = this.width + PADDING * 2
    } else {
      this.height = this.pointerHeight + PADDING * 2
    }

    const color = assignNewColorAndAlpha(FillType.Solid, this.color, this.opacity, props.color, props.opacity)
    this.color = <string>color.color
    this.opacity = color.opacity
  }

  protected applyProps(): Promise<void> {
    super.applyProps()
    lines[this.objectIndex].updateObject(this, <fabricTypes.Group><unknown>this.object)
    this.object.setCoords()
    return Promise.resolve()
  }

  onScaling(): boolean {
    const line = lines[this.objectIndex]

    const center = this.object.getCenterPoint()
    const angleRad = ((this.object.angle ?? 0) + (this.object.group?.angle ?? 0)) * (Math.PI / 180)
    const tl = this.object.getPointByOrigin('left', 'top').rotate(-angleRad, center)
    const br = this.object.getPointByOrigin('right', 'bottom').rotate(-angleRad, center)

    this.length = Math.max(Math.abs(tl.x - br.x), 0.01)
    this.height = Math.max(Math.abs(tl.y - br.y), 0.01)
    if (line.disablePointerHeight) {
      this.width = Math.max(this.height - PADDING * 2, 1)
    } else {
      this.pointerHeight = Math.max(this.height - PADDING * 2, 0)
      if (this.pointerHeight < this.width) {
        this.width = this.pointerHeight
      }
    }

    lines[this.objectIndex].updateObject(this, <fabricTypes.Group><unknown>this.object)
    return true
  }

  getCanvasManagedProperties(): ActualVersion {
    return Object.assign(
      super.getCanvasManagedProperties(),
      {
        length: this.length,
        height: this.height,
        width: this.width,
        pointerHeight: this.pointerHeight,
      },
    ) as ActualVersion
  }

  toObject(): object {
    const data = super.toObject()
    data.version = ActualVersionNumber
    return data
  }

  get bodyColour(): Rgba {
    return parseRgba(this.color)
  }
}
